'use client'

import { useMemo } from 'react'
import { useParams, usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'

import { useShallow } from 'zustand/react/shallow'

import { LAYOUT_TEST_IDS } from '@common/common-test-ids.const'
import { DEFAULT_HELP_ITEMS } from '@common/components/HelpMenu'
import { buildPortalHelpItems } from '@common/components/HelpMenu/mappers/buildPortalHelpItems'
import { NoProjectsFallback } from '@common/components/NoProjectsFallback'
import { NotificationToast } from '@common/components/NotificationDrawer'
import { useInitializationContext } from '@common/contexts/InitializationContext'
import { RightDrawerContext } from '@common/contexts/right-drawer-context'
import { AppVariant } from '@common/enums'
import { isDevEnvironment, isForceGraftcodeOnboardingEnabled } from '@common/helpers'
import { useAuthUser, useBreakpoint } from '@common/hooks'

import { GraftcodeOnboarding } from '@apps/graftcode-onboarding'
import { PortalOnboarding } from '@apps/portal-onboarding'
import { selectProjectsShellState } from '@modules/projects/stores/projects.selector'
import { useProjectsStore } from '@modules/projects/stores/projects.store'
import { selectWorkspacesShellState } from '@modules/workspaces/stores/workspaces.selector'
import { useWorkspacesStore } from '@modules/workspaces/stores/workspaces.store'
import { selectGlobalLoading } from '@stores/layout/layout.selector'
import { useLayoutStore } from '@stores/layout/layout.store'

import { registerLayoutModuleSubscriptions } from '../../utils/registerLayoutModuleSubscriptions'

import type { BaseLayoutClientProps } from './baseLayoutClient.types'
import { LayoutDialogHost } from './dialogs/LayoutDialogHost'
import { useLayoutMenuItems } from './hooks/useLayoutMenuItems'
import { useLayoutRouteState } from './hooks/useLayoutRouteState'
import { useNotificationDrawer } from './hooks/useNotificationDrawer'
import { useRightDrawer } from './hooks/useRightDrawer'
import { LayoutTopbarHost } from './hosts/LayoutTopbarHost'
import { LayoutMobileMenu } from './LayoutMobileMenu'
import { LayoutSidebar } from './LayoutSidebar'

import '@styles/layout/layout.scss'

registerLayoutModuleSubscriptions()

export function BaseLayoutClient({
  children,
  selectedNavId = AppVariant.PORTAL,
  noProjectFallback,
  searchSlot,
  searchOverlay,
}: BaseLayoutClientProps) {
  const pathname = usePathname()
  const { locale } = useParams()
  const tPortalOnboarding = useTranslations('portalOnboarding')
  const { isDesktop } = useBreakpoint()
  const layoutIsLoading = useLayoutStore(selectGlobalLoading)
  const sidebarCollapsed = useLayoutStore((state) => state.sidebarCollapsed)
  const openCreateProjectDialog = useLayoutStore((state) => state.openCreateProjectDialog)
  const { currentWorkspace, loading: workspacesLoading } = useWorkspacesStore(useShallow(selectWorkspacesShellState))
  const { projects, currentProject, loading: projectsLoading } = useProjectsStore(useShallow(selectProjectsShellState))

  const isLoading = layoutIsLoading || workspacesLoading || projectsLoading

  const { isActivityLogRoute, isProjectSettingsRoute, isServiceMapRoute, isSettingsRoute, isVisionRoute } =
    useLayoutRouteState({
      pathname,
    })

  const { sidebarMenuItems, mobileMenuItems } = useLayoutMenuItems({
    selectedNavId,
    isSettingsRoute,
    isVisionRoute,
    pathname,
    locale: locale as string,
    currentWorkspace,
    workspaceDataPending: workspacesLoading || layoutIsLoading,
  })

  const notificationsEnabled = isDevEnvironment()
  const { rightDrawerContent, drawerPhase, rightDrawerRef, openRightDrawer, closeRightDrawer } = useRightDrawer({
    pathname,
  })
  const { openNotificationDrawer } = useNotificationDrawer({ openRightDrawer, closeRightDrawer })

  const rightDrawerContextValue = useMemo(
    () => ({
      isOpen: rightDrawerContent !== null,
      content: rightDrawerContent,
      openRightDrawer,
      closeRightDrawer,
    }),
    [closeRightDrawer, openRightDrawer, rightDrawerContent]
  )

  const hasNoProjects = !!currentWorkspace && !isLoading && !projects.length && !currentProject
  const shouldShowNoProjects = hasNoProjects && (!isSettingsRoute || isProjectSettingsRoute)
  const { isEmailVerified } = useInitializationContext()
  const { user: auth0User } = useAuthUser()
  const isEmailConfirmed = isEmailVerified || auth0User?.email_verified === true
  const forceGraftcodeOnboarding = isForceGraftcodeOnboardingEnabled()
  const isPortalShellReady = selectedNavId === AppVariant.PORTAL && isEmailConfirmed && isDesktop
  const showGraftcodeOnboarding =
    selectedNavId === AppVariant.PORTAL && isDesktop && (isPortalShellReady || forceGraftcodeOnboarding)
  const helpItems = useMemo(() => {
    if (selectedNavId !== AppVariant.PORTAL || !isDesktop) return DEFAULT_HELP_ITEMS

    return buildPortalHelpItems(tPortalOnboarding('helpMenu.portalOnboarding'))
  }, [isDesktop, selectedNavId, tPortalOnboarding])

  const content =
    shouldShowNoProjects && noProjectFallback ? (
      <NoProjectsFallback {...noProjectFallback} onCreateProjectClick={openCreateProjectDialog} />
    ) : (
      children
    )

  return (
    <RightDrawerContext value={rightDrawerContextValue}>
      <div className={`app-layout ${sidebarCollapsed ? 'collapsed' : ''}`} data-testid={LAYOUT_TEST_IDS.root}>
        <LayoutDialogHost />
        <LayoutSidebar
          selectedNavId={selectedNavId}
          sidebarMenuItems={sidebarMenuItems}
          isSettingsRoute={isSettingsRoute}
          isVisionRoute={isVisionRoute}
        />

        <div
          className={`main-content ${drawerPhase !== 'idle' ? 'is-drawer-animating' : ''} ${rightDrawerContent ? 'is-drawer-open' : ''} ${isActivityLogRoute ? 'activity-log-page' : ''} ${isServiceMapRoute ? 'service-map-page' : ''}`}
        >
          <header className="topbar">
            <LayoutTopbarHost
              selectedNavId={selectedNavId}
              helpItems={helpItems}
              searchSlot={searchSlot}
              onNotificationClick={openNotificationDrawer}
            />
          </header>

          <div className="content-shell">
            <main className="content">{content}</main>
          </div>

          {rightDrawerContent ? (
            <aside
              ref={rightDrawerRef}
              className={`layout-right-drawer ${drawerPhase === 'closing' ? 'is-closing' : ''} ${drawerPhase !== 'idle' ? 'is-animating' : ''}`}
              data-testid={LAYOUT_TEST_IDS.registryDrawer}
            >
              {rightDrawerContent}
            </aside>
          ) : null}
        </div>

        <LayoutMobileMenu
          appVariant={selectedNavId}
          menuGroups={mobileMenuItems}
          hideAddNew={isSettingsRoute}
          onNotificationClick={openNotificationDrawer}
        />

        {notificationsEnabled && !rightDrawerContent ? <NotificationToast /> : null}
        {searchOverlay}
        {showGraftcodeOnboarding ? <GraftcodeOnboarding enabled /> : null}
        {selectedNavId === AppVariant.PORTAL ? <PortalOnboarding autoStart={false} /> : null}
      </div>
    </RightDrawerContext>
  )
}
